/**
 * @fileoverview Resolves the route a signed-in user should land on right after authenticating.
 * @description Replaces the old single fixed "dashboard route" (`authConfig.dashboardRoute`), which
 * assumed every signed-in user could reach the Sales dashboard — true back when Sales was the only
 * module, but wrong now that Sales and Banking are two separately privilege-gated modules with no
 * overlap: a Banking-only user has zero `sales:*` privileges, so sending them to the Sales
 * dashboard's route landed them on a page `RequirePrivilege` immediately rejects, bouncing them to
 * /403 right after login. This resolves the same "first nav item this user can actually open" the
 * sidebar itself would show, using the identical `NAV_ITEMS` list/order `NavMenu` filters by — so
 * "home" is always a page the signed-in user can actually reach.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { NAV_ITEMS } from '@/shared/constants/nav-items.constants'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { AuthUser } from '@/shared/lib/auth/auth.types'

/**
 * @description The route a signed-in user should land on right after authenticating, or when
 * redirected away from a guest-only/error page — the first `NAV_ITEMS` entry their privileges
 * unlock, in the sidebar's own order.
 * @param {string[]} privileges - The signed-in user's privileges.
 * @returns {string} The first nav route this user can open, or `ROUTES.SETTINGS` (unguarded for
 * every signed-in user) if none of their privileges match any nav item.
 */
export function resolveHomeRoute(privileges: string[]): string {
  const firstAccessible = NAV_ITEMS.find((item) => privileges.includes(item.privilege))
  return firstAccessible?.path ?? ROUTES.SETTINGS
}

/**
 * @description The single source of truth for "where should this signed-in user actually be
 * right now" — every route guard that redirects an authenticated visitor (TwoFactorRoute,
 * GuestRoute, RootRedirect) must call this rather than resolveHomeRoute directly, and so must
 * useVerifyTwoFactor's own post-verify navigate. A user who still must change their password is
 * sent to ROUTES.CHANGE_PASSWORD regardless of privileges — otherwise, since TwoFactorRoute's own
 * `isAuthenticated` check re-renders the instant useVerifyTwoFactor's setUser flips it true (before
 * that hook's own explicit navigate to CHANGE_PASSWORD has actually committed), this exact
 * redirect could win the race and bounce a mustChangePassword user straight past the forced change
 * to their normal home route instead.
 * @param {AuthUser | null | undefined} user - The signed-in user, or null/undefined if there
 * somehow isn't one yet (falls back to resolveHomeRoute's own empty-privileges default).
 * @returns {string} ROUTES.CHANGE_PASSWORD if the user must change their password, otherwise their
 * resolved home route.
 */
export function resolveSignedInDestination(user: AuthUser | null | undefined): string {
  if (user?.mustChangePassword) {
    return ROUTES.CHANGE_PASSWORD
  }

  return resolveHomeRoute(user?.privileges ?? [])
}
